import React, { useState, useEffect } from "react";
import { Row, Col, FormGroup, Label, Input, Button, Table } from "reactstrap";
import CreatableSelect from "react-select/creatable";
import Select from "react-select";
import {
  getDriverDeductionByAll,
  createDeductionType,
  saveBulkDriverDeductions
} from "../../../Services/Driver.service";
import { showAlert } from "../../../Services/Shared.service";
import TrashIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import { Spinner } from "../../../AbstractElements";

const VALUE_TYPE_OPTIONS = [
  { label: "Percentage (%)", value: "P" },
  { label: "Amount (₹)", value: "A" },
];

const CALC_TYPE_OPTIONS = [
  { label: "BaseFare", value: "BaseFare" },
  { label: "TotalFare", value: "TotalFare" },
  { label: "WithAddCharge", value: "WithAddCharge" },
  { label: "None", value: "None" }
];

const BOOKING_TYPE_OPTIONS = [
  { label: "MobileApp", value: "MobileApp" },
  { label: "DriverApp", value: "DriverApp" },
  { label: "Website", value: "Website" },
  { label: "PartnerApi", value: "PartnerApi" },
  { label: "CustomerService", value: "CustomerService" },
  { label: "IRCTC", value: "IRCTC" },
  { label: "BookingWeb", value: "BookingWeb" },
  { label: "JustDial", value: "JustDial" },
  { label: "Integrator", value: "Integrator" },
  { label: "AirportBooking", value: "AirportBooking" },
  { label: "City", value: "City" },
  { label: "CRON", value: "CRON" },
  { label: "All", value: "All" },
  { label: "Others", value: "Others" },
  { label: "CallCenter", value: "CallCenter" },
];

const BulkDeductionForm = ({ driverScheme, onClose, onRefresh }) => {
  const [loading, setLoading] = useState(false);
  const [existingTypes, setExistingTypes] = useState([]);
  const [focusedRowIndex, setFocusedRowIndex] = useState(null);
  const [rows, setRows] = useState([
    {
      deductionType: null,
      valueType: VALUE_TYPE_OPTIONS[0],
      value: "",
      calculationType: CALC_TYPE_OPTIONS[0],
      bookingType: BOOKING_TYPE_OPTIONS.find(o => o.value === "All"),
    },
  ]);

  useEffect(() => {
    fetchTypes();
  }, []);

  const fetchTypes = async () => {
    try {
      const resp = await getDriverDeductionByAll();
      // Updated to match the provided searchResponse structure
      const dataArr = resp.data?.searchResponse || resp.searchResponse || [];
      const types = dataArr.map((t) => ({
        label: t.deductionType,
        value: t.id,
      }));
      console.log(dataArr, "TYPES")
      setExistingTypes(types);
    } catch (e) {
      console.error("Error fetching types", e);
    }
  };

  const handleAddRow = () => {
    setRows([
      ...rows,
      {
        deductionType: null,
        valueType: VALUE_TYPE_OPTIONS[0],
        value: "",
        calculationType: CALC_TYPE_OPTIONS[0],
        bookingType: BOOKING_TYPE_OPTIONS.find(o => o.value === "All"),
      },
    ]);
  };

  const handleRemoveRow = (index) => {
    if (rows.length > 1) {
      const updatedRows = rows.filter((_, i) => i !== index);
      setRows(updatedRows);
    }
  };

  const handleInputChange = (index, field, value) => {
    const updatedRows = [...rows];
    updatedRows[index][field] = value;
    setRows(updatedRows);
  };

  const handleSubmit = async () => {
    // 1. Check for duplicates within the current rows (Client-side)
    const combinations = new Set();
    for (const row of rows) {
      if (!row.deductionType || !row.value) {
        showAlert("error", "Please fill in all Deduction Types and Values");
        return;
      }
      const comboKey = `${row.deductionType.value}-${row.bookingType.value}`;
      if (combinations.has(comboKey)) {
        showAlert("error", `Duplicate entry found for ${row.deductionType.label} with booking type ${row.bookingType.label}`);
        return;
      }
      combinations.add(comboKey);
    }

    setLoading(true);
    try {
      const deductionList = [];
      for (const row of rows) {
        let typeId = row.deductionType.value;

        if (row.deductionType.__isNew__) {
          const typeResp = await createDeductionType({
            deductionType: row.deductionType.label,
            deductionDisplayName: row.deductionType.label,
            calculateOn: row.calculationType.value
          });
          typeId = typeResp.data.id;
        }

        deductionList.push({
          driverSchemeId: driverScheme.id,
          serviceCityId: driverScheme.serviceCityId,
          driverDeductionId: typeId,
          valueType: row.valueType.value,
          value: parseFloat(row.value),
          calculateType: row.calculationType.value,
          bookingTypeDeduction: row.bookingType.value,
        });
      }

      await saveBulkDriverDeductions(deductionList);

      showAlert("success", "All deductions added successfully!");
      onRefresh();
      onClose();
    } catch (error) {
      console.error("Error saving deductions:", error);
      const msg = error.response?.data?.message || "Failed to save some deductions.";
      showAlert("error", msg);
    } finally {
      setLoading(false);
    }
  };

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      borderRadius: "8px",
      fontSize: "13px",
      minHeight: "38px",
      backgroundColor: state.isDisabled ? "#e9ecef" : "white",
      color: state.isDisabled ? "#6c757d" : "#333",
      borderColor: state.isFocused ? "#39B54A" : "#dee2e6",
      boxShadow: state.isFocused ? "0 0 0 0.2rem rgba(57, 181, 74, 0.25)" : "none",
      cursor: state.isDisabled ? "not-allowed" : "default",
      "&:hover": {
        borderColor: state.isFocused ? "#39B54A" : (state.isDisabled ? "#dee2e6" : "#ced4da")
      }
    }),
    singleValue: (base, state) => ({
      ...base,
      color: state.isDisabled ? "#6c757d" : "#333"
    }),
    placeholder: (base) => ({
      ...base,
      color: "#999"
    }),
    menu: (base) => ({
      ...base,
      zIndex: 9999
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#39B54A"
        : state.isFocused
          ? "#E3F5E1"
          : "white",
      color: state.isSelected ? "white" : "#333",
      fontSize: "13px",
      cursor: "pointer",
      "&:active": {
        backgroundColor: "#39B54A",
        color: "white"
      }
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 })
  };

  return (
    <div className="p-1">
      <div style={{ overflowX: "auto", width: "100%" }}>
        <Table borderless style={{ minWidth: "900px", overflow: "visible" }}>
          <thead>
            <tr className="text-muted small text-uppercase">
              <th style={{ width: "25%" }}>Deduction Type</th>
              <th style={{ width: "18%" }}>Value Type</th>
              <th style={{ width: "12%" }}>Value</th>
              <th style={{ width: "18%" }}>Calc Type</th>
              <th style={{ width: "22%" }}>Booking Type</th>
              <th style={{ width: "5%" }}></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={index}>
                <td>
                  <CreatableSelect
                    isClearable
                    options={existingTypes}
                    value={row.deductionType}
                    onChange={(val) => handleInputChange(index, "deductionType", val)}
                    isValidNewOption={(inputValue) => inputValue.trim().length > 0}
                    placeholder="Select/Create..."
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                  />
                </td>
                <td>
                  <Select
                    options={VALUE_TYPE_OPTIONS}
                    value={row.valueType}
                    onChange={(val) => handleInputChange(index, "valueType", val)}
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                  />
                </td>
                <td>
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={row.value}
                    onChange={(e) => handleInputChange(index, "value", e.target.value)}
                    onFocus={() => setFocusedRowIndex(index)}
                    onBlur={() => setFocusedRowIndex(null)}
                    className="form-control"
                    style={{
                      borderRadius: "8px",
                      fontSize: "13px",
                      height: "38px",
                      backgroundColor: "white",
                      color: "#333",
                      borderColor: focusedRowIndex === index ? "#39B54A" : "#dee2e6",
                      boxShadow: focusedRowIndex === index ? "0 0 0 0.2rem rgba(57, 181, 74, 0.25)" : "none",
                      outline: "none"
                    }}
                  />
                </td>
                <td>
                  <Select
                    options={CALC_TYPE_OPTIONS}
                    value={row.calculationType}
                    onChange={(val) => handleInputChange(index, "calculationType", val)}
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                    isDisabled={row.deductionType && !row.deductionType.__isNew__}
                  />
                </td>
                <td>
                  <Select
                    options={BOOKING_TYPE_OPTIONS}
                    value={row.bookingType}
                    onChange={(val) => handleInputChange(index, "bookingType", val)}
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                  />
                </td>
                <td className="text-center align-middle">
                  <Button
                    color="link"
                    className="text-danger p-0"
                    onClick={() => handleRemoveRow(index)}
                    disabled={rows.length === 1}
                  >
                    <TrashIcon />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      <div className="d-flex justify-content-between mt-4">
        <Button
          color="light"
          style={{
            borderRadius: "10px",
            fontWeight: "600",
            border: "1px solid #ddd",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "8px 20px"
          }}
          onClick={handleAddRow}
        >
          <AddIcon fontSize="small" style={{ marginRight: "8px" }} />
          Add More
        </Button>

        <Button
          color="primary"
          style={{
            borderRadius: "10px",
            fontWeight: "600",
            padding: "10px 30px",
            backgroundColor: "#39B54A",
            border: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? (
            <Spinner size="sm" style={{ marginRight: "8px" }} />
          ) : (
            <SaveIcon fontSize="small" style={{ marginRight: "8px" }} />
          )}
          Save All Deductions
        </Button>
      </div>
    </div>
  );
};

export default BulkDeductionForm;
